import { action, computed, observable } from 'mobx'
import {
  getRoomState,
  getRoomWasGraded,
  updateRoomState,
} from '../firestore/RoomState'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { RoomState } from '../models/RoomState'
import { Cubit } from './core'
import { DateTime } from 'luxon'
import { PublicUser } from '../models/PublicUser'
import {
  RoomStateDemoType,
  RoomStateVideoMethod,
} from '../firestore/RoomState/types'

export class RoomStateCubit extends Cubit {
  roomId: string
  repository: FirebaseRepository
  roomState: RoomState
  roomWasGraded = observable.box<null | boolean>(null)

  constructor(repository: FirebaseRepository, roomId: string | undefined) {
    super()
    this.repository = repository
    this.roomId = roomId || ''
    this.roomState = RoomState.empty(repository)
  }

  initialize(): void {
    // we don't want to load the room state if the room id is not set
    // set loading to false and show NotFound page
    if (!this.roomId) {
      this.roomWasGraded.set(false)
      this.roomState.changeLoadingState(false)
      return
    }
    this.addStream(
      getRoomState(this.repository, { roomStateId: this.roomId }),
      (newRoomState) => {
        if (newRoomState.data === undefined) return

        this.roomState.replaceModel(newRoomState)
      }
    )

    this.addStream(
      getRoomWasGraded(this.repository, { roomStateId: this.roomId }),
      (wasGraded) => {
        this.roomWasGraded.set(wasGraded)
        // only need to check once, don't want to change join / view results button after initial render
        this.removeStream('roomWasGraded')
      },
      {
        name: 'roomWasGraded',
      }
    )
  }

  @action
  ensureVideoMethod(expectedMethod: RoomStateVideoMethod) {
    if (this.roomState.data?.videoMethod === expectedMethod) {
      return Promise.resolve()
    }

    return updateRoomState(this.repository.firestore, this.roomId, {
      videoMethod: expectedMethod,
    })
  }

  @action startSoloDemo() {
    return this.roomState.startRoomIfNotStarted()
  }

  @computed
  get ready() {
    return this.roomState.isLoaded && this.roomWasGraded.get() !== null //null is initial, set to bool after load
  }

  @computed
  get roomIsStale() {
    const activeSlideChangedAt = this.roomState.data.activeSlideChangedAt
      ? DateTime.fromJSDate(this.roomState.data.activeSlideChangedAt)
      : null
    if (!activeSlideChangedAt) return false

    // Check if the last change slide is older than 30 minutes.
    const staleTime = 30 * 60 * 1000 // 30 minutes
    const roomIsStale =
      activeSlideChangedAt.plus({ milliseconds: staleTime }) < DateTime.now()

    return roomIsStale
  }

  @computed
  get groupLeaderUser() {
    const groupLeaderUserIds = this.roomState.data?.groupLeaderUserIds
    const id = groupLeaderUserIds[0]
    if (!id) return PublicUser.empty(this.repository)

    return this.repository.userStore.getUser(id)
  }

  // Ex. 2/21/24 - 12:00PM
  @computed
  get formattedDate() {
    if (!this.roomState.data?.scheduledAt) return ''
    const dateTime = DateTime.fromJSDate(this.roomState.data.scheduledAt)

    return dateTime.toFormat('M/d/yy - h:mma')
  }

  @computed
  get isSoloDemo() {
    const userIds = this.roomState.data?.userIds || []

    if (userIds.length !== 1) return false

    const first = userIds[0]

    // (ashold12): this was the original check for solo demo
    // not sure this is really necessary anymore but belt and suspenders approach
    const isAloneInRoom = first === this.repository.uid

    // (ashold12) if an admin joins the room the solo demo user should not be blocked from starting
    return (
      isAloneInRoom || this.roomState.data.demoType === RoomStateDemoType.solo
    )
  }
}
